/**
 * CHAT HISTORY PROCESSOR
 * Backend-integrated service for chat history management
 * Uses server-side storage and LLM processing
 */

import type { ChatMessage } from '../types/ChatMessage';
import {
  WeekGroup,
  MonthGroup,
  ChatHistory,
  WeekType,
  WeekStats,
  WeekProcessingResult,
  WeekUtils
} from '../types/ChatHistory';
import { storage } from './storage';
import { formatWeekRange as formatWeekRangeSafe } from '../utils/chatHistoryGrouping';
import { toLocalDateStr } from '../utils/sharedFormatters';

import { API_BASE_URL as CENTRALIZED_API_URL } from '../utils/api';

/** Backend week response — supports both camelCase and snake_case fields */
interface BackendWeekResponse {
  weekId?: string;
  week_id?: string;
  weekStart?: string;
  week_start_date?: string;
  weekEnd?: string;
  week_end_date?: string;
  summary?: string;
  emoji?: string;
  weekType?: string;
  week_type?: string;
  totalMessages?: number;
  total_messages?: number;
  isProcessed?: boolean;
  is_processed?: boolean;
  stats?: BackendWeekStats;
  keyMoments?: Array<string | { content_preview?: string; message_preview?: string }>;
  messageIds?: string[];
}

interface BackendWeekStats {
  totalMessages?: number;
  total_messages?: number;
  goalsCreated?: number;
  goals_created?: number;
  workoutsCompleted?: number;
  workouts_completed?: number;
  breakthroughMoments?: number;
  breakthrough_moments?: number;
  userMessages?: number;
  user_messages?: number;
  assistantMessages?: number;
  assistant_messages?: number;
  totalWords?: number;
  total_words?: number;
}

interface BackendMonthResponse {
  monthId?: string;
  month_id?: string;
  year: number;
  month: number;
  monthName?: string;
  month_name?: string;
  totalWeeks?: number;
  total_weeks?: number;
  totalMessages?: number;
  total_messages?: number;
  totalWords?: number;
  total_words?: number;
  summary?: string;
  weeks?: BackendWeekResponse[];
}

interface BackendChatHistoryResponse {
  months?: BackendMonthResponse[];
  currentWeek?: BackendWeekResponse;
  isProcessing?: boolean;
  lastProcessed?: string;
}

interface BackendMessageResponse {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export class ChatHistoryProcessor {
  private static readonly API_BASE_URL = CENTRALIZED_API_URL;

  /**
   * Get authentication headers
   */
  private static async getAuthHeaders() {
    const token = await storage.getItem('jwt_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  /**
   * Transform a backend week object into the frontend WeekGroup shape
   */
  private static transformWeek(backendWeek: BackendWeekResponse): WeekGroup {
    const startDate = backendWeek.weekStart || backendWeek.week_start_date || '';
    const endDate = backendWeek.weekEnd || backendWeek.week_end_date || '';
    const dateRange = startDate && endDate
      ? this.formatWeekRange(startDate, endDate)
      : '';
    const stats = backendWeek.stats || {};

    return {
      id: backendWeek.weekId || backendWeek.week_id || '',
      dateRange,
      startDate,
      endDate,
      summary: backendWeek.summary || '',
      emoji: backendWeek.emoji || '📅',
      weekType: (backendWeek.weekType || backendWeek.week_type || 'routine') as WeekType,
      stats: {
        messageCount: stats.totalMessages ?? stats.total_messages ?? backendWeek.totalMessages ?? 0,
        goalsCreated: stats.goalsCreated ?? stats.goals_created ?? 0,
        workoutsCompleted: stats.workoutsCompleted ?? stats.workouts_completed ?? 0,
        breakthroughMoments: stats.breakthroughMoments ?? stats.breakthrough_moments ?? 0,
      },
      keyMoments: Array.isArray(backendWeek.keyMoments)
        ? backendWeek.keyMoments.map((m) => typeof m === 'string' ? m : m.content_preview || m.message_preview || '')
        : [],
      messageIds: backendWeek.messageIds || [],
      isLoaded: false,
      isExpanded: false,
    };
  }

  /**
   * Transform a backend month object into the frontend MonthGroup shape
   */
  private static transformMonth(backendMonth: BackendMonthResponse): MonthGroup {
    const weeks = (backendMonth.weeks || [])
      .filter((w): w is BackendWeekResponse => !!w && !!(w.weekId || w.week_id))
      .map((w) => this.transformWeek(w));

    return {
      id: backendMonth.monthId || backendMonth.month_id || `month_${backendMonth.year}_${backendMonth.month}`,
      label: (backendMonth.monthName || backendMonth.month_name || '').toUpperCase(),
      year: backendMonth.year,
      month: (backendMonth.month ?? 1) - 1, // Backend is 1-12, frontend is 0-11
      isExpanded: true, // Default expanded for recent months
      weeks,
      summary: backendMonth.summary,
    };
  }

  /**
   * Get current chat history from backend
   */
  static async getChatHistory(): Promise<ChatHistory> {
    try {
      console.log('HISTORY: Fetching chat history from backend...');

      const response = await fetch(`${this.API_BASE_URL}/api/chat-history/`, {
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch chat history: ${response.status}`);
      }

      const data: BackendChatHistoryResponse = await response.json();
      console.log('HISTORY: Successfully fetched chat history from backend');

      // Transform backend response into frontend types
      const months = (data.months || []).map((m) => this.transformMonth(m));

      let currentWeek: WeekGroup | null = null;
      if (data.currentWeek) {
        const cw = data.currentWeek;
        const cwStats = cw.stats || {};
        // currentWeek from backend — build a full WeekGroup
        // Use backend-provided dates when available, fall back to local calculation
        const startDate = cw.weekStart || cw.week_start_date || '';
        const endDate = cw.weekEnd || cw.week_end_date || '';
        let dateRange: string;
        if (startDate && endDate) {
          dateRange = this.formatWeekRange(startDate, endDate);
        } else {
          // Local-midnight Dates, so format from their LOCAL Y-M-D.
          // `toISOString()` would re-project them to UTC and shift the
          // day for anyone east of UTC — the mirror of the bug fixed in
          // formatWeekRange.
          const now = new Date();
          const { start, end } = this.getWeekBoundaries(now);
          dateRange = this.formatWeekRange(toLocalDateStr(start), toLocalDateStr(end));
        }
        const totalMessages = cwStats.totalMessages ?? cwStats.total_messages ?? cw.totalMessages ?? cw.total_messages ?? 0;
        const goalsCreated = cwStats.goalsCreated ?? cwStats.goals_created ?? 0;
        const workoutsCompleted = cwStats.workoutsCompleted ?? cwStats.workouts_completed ?? 0;

        // Build a meaningful fallback summary when none is provided
        let fallbackSummary = '';
        if (cw.summary) {
          fallbackSummary = cw.summary;
        } else {
          const parts: string[] = [];
          if (totalMessages > 0) parts.push(`${totalMessages} messages`);
          if (goalsCreated > 0) parts.push(`${goalsCreated} goal${goalsCreated > 1 ? 's' : ''} created`);
          if (workoutsCompleted > 0) parts.push(`${workoutsCompleted} workout${workoutsCompleted > 1 ? 's' : ''} completed`);
          fallbackSummary = parts.length > 0
            ? `This week: ${parts.join(', ')}`
            : 'Your week so far';
        }

        currentWeek = {
          id: cw.weekId || cw.week_id || '',
          dateRange,
          startDate,
          endDate,
          summary: fallbackSummary,
          emoji: cw.emoji || '📅',
          weekType: 'routine' as WeekType,
          stats: {
            messageCount: totalMessages,
            goalsCreated: cwStats.goalsCreated ?? cwStats.goals_created ?? 0,
            workoutsCompleted: cwStats.workoutsCompleted ?? cwStats.workouts_completed ?? 0,
            breakthroughMoments: cwStats.breakthroughMoments ?? cwStats.breakthrough_moments ?? 0,
          },
          keyMoments: [],
          messageIds: [],
          isLoaded: false,
          isExpanded: false,
        };
      }

      return {
        months,
        currentWeek,
        isProcessing: data.isProcessing || false,
        lastProcessed: data.lastProcessed || new Date().toISOString()
      };
    } catch (error) {
      console.error('HISTORY: Failed to fetch chat history:', error);
      // Return empty history structure as fallback
      return {
        months: [],
        currentWeek: null,
        isProcessing: false,
        lastProcessed: new Date().toISOString()
      };
    }
  }

  /**
   * Save chat history to backend (deprecated - now handled server-side)
   */
  static async saveChatHistory(history: ChatHistory): Promise<void> {
    // This method is now deprecated as history is managed server-side
    console.log('HISTORY: saveChatHistory is deprecated - history managed server-side');
  }

  /**
   * Check if we need to create a new week (now handled server-side)
   */
  static async checkForNewWeek(): Promise<boolean> {
    // This is now handled automatically server-side when messages are added
    console.log('HISTORY: checkForNewWeek is handled server-side');
    return false;
  }

  /**
   * Process messages into weekly rollup using backend LLM
   */
  static async processWeeklyRollup(messages: ChatMessage[]): Promise<void> {
    try {
      console.log('HISTORY: Starting weekly rollup processing...');
      
      // Extract message texts for LLM analysis
      const messageTexts = messages.map(msg => msg.text).filter(Boolean);
      
      const response = await fetch(`${this.API_BASE_URL}/api/chat-history/process-week`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({
          messages: messageTexts,
          messageCount: messages.length
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to process week: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('HISTORY: Weekly rollup processing queued successfully');
      } else {
        console.error('HISTORY: Weekly rollup processing failed:', result.message);
      }

    } catch (error) {
      console.error('HISTORY: Error processing weekly rollup:', error);
    }
  }

  /**
   * Get messages for a specific week
   */
  static async getWeekMessages(weekId: string): Promise<ChatMessage[]> {
    try {
      console.log(`HISTORY: Fetching messages for week ${weekId}...`);
      
      const response = await fetch(`${this.API_BASE_URL}/api/chat-history/week/${weekId}/messages`, {
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch week messages: ${response.status}`);
      }

      const data: { messages: BackendMessageResponse[] } = await response.json();
      console.log(`HISTORY: Successfully fetched ${data.messages.length} messages for week`);

      // Convert backend message format to frontend ChatMessage format
      return data.messages.map((msg) => ({
        id: msg.id,
        sender: msg.sender,
        text: msg.content,
        timestamp: msg.timestamp,
        status: 'delivered' as const,
        metadata: msg.metadata || {}
      }));

    } catch (error) {
      console.error('HISTORY: Failed to fetch week messages:', error);
      return [];
    }
  }

  /**
   * Get processing status
   */
  static async getProcessingStatus(): Promise<{
    isProcessing: boolean;
    pendingWeeks: number;
    lastProcessed: string | null;
    currentWeekMessages: number;
  }> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/chat-history/processing-status`, {
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to get processing status: ${response.status}`);
      }

      const data = await response.json();
      return {
        isProcessing: data.isProcessing || false,
        pendingWeeks: data.pendingWeeks || 0,
        lastProcessed: data.lastProcessed || null,
        currentWeekMessages: data.currentWeekMessages || 0
      };

    } catch (error) {
      console.error('HISTORY: Failed to get processing status:', error);
      return {
        isProcessing: false,
        pendingWeeks: 0,
        lastProcessed: null,
        currentWeekMessages: 0
      };
    }
  }

  // =====================================================
  // LEGACY METHODS (Maintained for compatibility)
  // =====================================================

  /**
   * Finalize a week (now handled server-side)
   */
  private static async finalizeWeek(week: WeekGroup, allMessages: ChatMessage[]): Promise<void> {
    console.log('HISTORY: finalizeWeek is handled server-side automatically');
  }

  /**
   * Create new current week (now handled server-side)
   */
  private static async createNewCurrentWeek(): Promise<void> {
    console.log('HISTORY: createNewCurrentWeek is handled server-side automatically');
  }

  /**
   * Generate week summary (now handled server-side with GPT-4o-mini)
   */
  private static async generateWeekSummary(messages: ChatMessage[]): Promise<WeekProcessingResult> {
    console.log('HISTORY: generateWeekSummary is handled server-side with LLM');
    
    // Return default result for compatibility
    return {
      weekId: 'processing',
      summary: 'Processing...',
      emoji: '⏳',
      weekType: 'routine' as WeekType,
      stats: {
        messageCount: messages.length,
        goalsCreated: 0,
        workoutsCompleted: 0,
        breakthroughMoments: 0
      },
      keyMoments: []
    };
  }

  /**
   * Add message to current week (now handled server-side automatically)
   */
  static async addMessageToCurrentWeek(message: ChatMessage): Promise<void> {
    console.log('HISTORY: addMessageToCurrentWeek is handled server-side automatically');
  }

  // =====================================================
  // UTILITY METHODS
  // =====================================================

  /**
   * Get week boundaries (Monday to Sunday)
   */
  static getWeekBoundaries(date: Date): { start: Date; end: Date } {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  }

  /**
   * Format a week's date range for display.
   *
   * Delegates to the timezone-safe formatter in utils/chatHistoryGrouping.
   *
   * The previous implementation built `new Date(startDate)` from a
   * date-only string like "2026-08-10". That parses as UTC midnight, so
   * `.getDate()` read it back one day earlier for every user west of UTC
   * — the whole of the US and Canada. Monday-anchored weeks (guaranteed
   * by the `chat_history_weeks` CHECK constraint, migration 141) rendered
   * as Sunday→Saturday: Mon Aug 10 – Sun Aug 16 displayed "Aug 9-15".
   *
   * Keep this delegating. Do not reintroduce a Date here.
   */
  static formatWeekRange(startDate: string, endDate: string): string {
    return formatWeekRangeSafe(startDate, endDate);
  }

  /**
   * Check if date is current week
   */
  static isCurrentWeek(weekStart: string): boolean {
    const weekStartDate = new Date(weekStart);
    const { start } = this.getWeekBoundaries(new Date());
    return weekStartDate.getTime() === start.getTime();
  }
}